import { t } from "i18next";
import { get, isNil } from "lodash";
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSetRecoilState } from "recoil";

import {
  ApiSuffixType,
  CrudApi,
  DropdownItemsKeeperModel,
  DropdownItemsLoadingModel,
  DropdownItemsPromiseCacheModel,
  dropdownsIsFetchingAtom,
  getDropdownOptionLabel,
  IDropdownItem,
  IDropdownItemsParams,
  IExtendableDto,
  ISimpleDto,
  ISimplePassengerDto,
  Log,
  NO_VALUE_DISPLAY,
  RydeUiUnit,
  TDropdownItemsKeeperIndex,
  transformDropdownDtos,
  unique,
} from "@ryde/shared";

import { appendSuffixToEntity } from "../use-dynamic-forms.helpers";
import { useSetDropdownAtomFamily } from "./use-dropdown-atom-family-utils";

const DTO_PATH_TO_ENTITY: Record<string, RydeUiUnit> = {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- a DTO field path, not an identifier
  "metadata.tags": "systemTag",
  journeyId: "journey",
};

export interface IDtoDropdownInvalidate {
  path: string;
  dtoDropdownValueToDropdownItemValue: keyof IDropdownItem;
}

const invalidateResolve = ({
  dtoDropdownInvalidate,
  dto,
  keeper,
  invalidateCb,
}: {
  dto: Partial<IExtendableDto>;
  dtoDropdownInvalidate: IDtoDropdownInvalidate;
  keeper: DropdownItemsKeeperModel;
  invalidateCb: (entity: RydeUiUnit) => void;
}): void => {
  const { path, dtoDropdownValueToDropdownItemValue } = dtoDropdownInvalidate;

  const entity = DTO_PATH_TO_ENTITY[path];

  if (!entity) {
    Log.error(
      t("errors.passedPathHasNotCorrespondingEntityPath", {
        path: path,
        dto: dto,
      }),
    );
    return;
  }

  const dtoValue = get(dto, path, null);

  if (dtoValue === null) {
    return;
  }

  const isShouldValidate = !keeper?.[entity]
    ?.map(dropdownItem => dropdownItem[dtoDropdownValueToDropdownItemValue])
    .includes(dtoValue);

  if (!isShouldValidate) {
    return;
  }

  invalidateCb(entity);
};

export type IUseDropdownItemsCtxProvider = ReturnType<
  typeof useDropdownItemsCtxProvider
>;

export const useDropdownItemsCtxProvider = () => {
  const { setDropdownAtomFamily } = useSetDropdownAtomFamily();
  const setDropdownsLoading = useSetRecoilState(dropdownsIsFetchingAtom);
  const [keeper] = useState<DropdownItemsKeeperModel>(
    {} as DropdownItemsKeeperModel,
  );
  const [dropdownItemsKeeperIndex] = useState<TDropdownItemsKeeperIndex>(
    new Map(),
  );

  const [promiseCache] = useState<DropdownItemsPromiseCacheModel>(
    {} as DropdownItemsPromiseCacheModel,
  );
  const [itemsLoading] = useState<DropdownItemsLoadingModel>(
    {} as DropdownItemsLoadingModel,
  );
  const loadingDropdownItems = useMemo(() => {
    const loading = Object.values(itemsLoading).some(x => x);
    return loading;
  }, [itemsLoading]);

  useEffect(() => {
    setDropdownsLoading(loadingDropdownItems || false);
  }, [loadingDropdownItems, setDropdownsLoading]);

  const getDropdownItems = useCallback(
    async (
      entity: RydeUiUnit,
      forceFetch = false,
      params: IDropdownItemsParams = {},
    ): Promise<IDropdownItem[]> => {
      const entityCacheKey = appendSuffixToEntity({
        entity,
        suffix: params.dropdownItemsSuffixOverride,
      });

      if (forceFetch) {
        delete keeper[entityCacheKey];
      }

      if (keeper[entityCacheKey] && !forceFetch) {
        return keeper[entityCacheKey];
      }

      const suffix = params.dropdownItemsSuffixOverride
        ? ApiSuffixType.NoSuffix
        : ApiSuffixType.DropdownItems;

      let fetchPromise:
        Promise<(ISimpleDto | ISimplePassengerDto)[] | undefined> | undefined;

      if (promiseCache[entityCacheKey] !== undefined && !forceFetch) {
        fetchPromise = promiseCache[entityCacheKey];
      } else {
        fetchPromise = CrudApi.getAll<ISimpleDto | ISimplePassengerDto>(
          entity,
          suffix,
          params.dropdownItemsSuffixOverride,
        );
        promiseCache[entityCacheKey] = fetchPromise;
      }

      itemsLoading[entityCacheKey] = true;
      let dtos: (ISimpleDto | ISimplePassengerDto)[] | undefined;
      try {
        dtos = await fetchPromise;
      } catch (error) {
        // A refused request must not stay cached, or every later call for this entity gets the same failure.
        if (promiseCache[entityCacheKey] === fetchPromise) {
          delete promiseCache[entityCacheKey];
        }
        throw error;
      } finally {
        itemsLoading[entityCacheKey] = false;
      }

      if (promiseCache[entityCacheKey] !== fetchPromise) {
        itemsLoading[entityCacheKey] = false;
        if (keeper[entityCacheKey]) {
          return keeper[entityCacheKey];
        }
        const latestDtos = await promiseCache[entityCacheKey];
        return (
          transformDropdownDtos({
            dtos: latestDtos,
            entity,
            forceResolverName: params.dropdownItemsResolverName,
          }) || []
        );
      }

      const items: IDropdownItem[] | undefined = transformDropdownDtos({
        dtos,
        entity,
        forceResolverName: params.dropdownItemsResolverName,
      });
      itemsLoading[entityCacheKey] = false;

      if (!items) {
        keeper[entityCacheKey] = [];
        return [];
      }

      keeper[entityCacheKey] = items;

      setDropdownAtomFamily(entityCacheKey, items);

      return items;
    },
    [itemsLoading, keeper, promiseCache, setDropdownAtomFamily],
  );

  const getDropdownLabel = async (
    entity: RydeUiUnit,
    id: number | undefined,
  ): Promise<string> => {
    if (!id || entity === "unknown") {
      return NO_VALUE_DISPLAY;
    }

    const resolveLabel = async (forceFetch: boolean): Promise<string> => {
      const dropdownList = await getDropdownItems(entity, forceFetch);
      return (
        getDropdownOptionLabel({
          entity,
          dropdownList,
          id,
          dropdownItemsKeeperIndex,
        }) || NO_VALUE_DISPLAY
      );
    };

    const label = await resolveLabel(false);
    if (label !== NO_VALUE_DISPLAY) {
      return label;
    }

    return resolveLabel(true);
  };

  const isValueInDropdownList = async (
    entity: RydeUiUnit,
    value: number | undefined,
  ): Promise<boolean> => {
    if (!value || entity === "unknown") {
      return false;
    }

    const dropdownList = await getDropdownItems(entity);

    if (!dropdownList?.length) {
      return false;
    }

    return (
      !isNil(value) &&
      dropdownList?.some(
        x => !isNil(x.value) && String(value) === String(x.value),
      )
    );
  };

  const getDropdownLabels = async (
    entity: RydeUiUnit,
    ids: number[] | undefined,
  ): Promise<string[]> => {
    if (!ids?.length || entity === "unknown") {
      return [];
    }

    const labels = [];

    for (const id of ids) {
      const label = await getDropdownLabel(entity, id);
      labels.push(label);
    }

    return labels?.length ? unique(labels) : [];
  };

  const invalidateDropdownItemsFromDto = ({
    dto,
    dtoDropdownInvalidates,
  }: {
    dto: Partial<IExtendableDto>;
    dtoDropdownInvalidates: IDtoDropdownInvalidate[];
  }): void => {
    startTransition(() => {
      dtoDropdownInvalidates.forEach(dtoDropdownInvalidate =>
        invalidateResolve({
          dtoDropdownInvalidate,
          dto,
          keeper,
          invalidateCb: entity => getDropdownItems(entity, true),
        }),
      );
    });
  };

  return {
    getDropdownItems,
    getDropdownLabel,
    getDropdownLabels,
    invalidateDropdownItemsFromDto,
    isValueInDropdownList,
  };
};
